import type {
  SourceMaterial,
  SourceMaterialKind,
  SourceMaterialSummary,
} from '@mira/clients-shared';
import { useMemo, useRef, useState } from 'react';
import { createWebApiClient } from '../api/webSession.js';
import { showToast } from '../app/toast.js';
import { Button, IconGlyph } from '../ui/primitives/index.js';
import {
  maxConversationSources,
  SourceMaterialInputError,
  validateConfirmedSourceText,
  validateWebSourceFile,
} from './contracts.js';
import { extractSourceText, type SourceExtractionMethod } from './extract.js';
import { VoiceJournalRecorder } from './VoiceJournalRecorder.js';

export type LocalSourceStatus = 'uploading' | 'processing' | 'review' | 'ready' | 'failed' | 'removing';

export interface ConversationSourceDraft {
  localId: string;
  file: File;
  kind: SourceMaterialKind;
  mimeType: string;
  status: LocalSourceStatus;
  progress: number;
  statusLabel: string;
  text: string;
  extractionMethod: SourceExtractionMethod;
  sourceMaterial?: SourceMaterial;
  error?: string;
}

export interface ConversationSourceController {
  addFiles: (files: FileList | File[], expectedKind: 'file' | 'image') => void;
  addVoice: (file: File, transcript: string) => Promise<void>;
  blocksSend: boolean;
  confirm: (localId: string) => Promise<void>;
  consumeReady: () => void;
  editText: (localId: string, text: string) => void;
  items: ConversationSourceDraft[];
  readyIds: string[];
  remove: (localId: string) => Promise<void>;
  retry: (localId: string) => Promise<void>;
}

export function useConversationSourceMaterials(disabled = false): ConversationSourceController {
  const [items, setItems] = useState<ConversationSourceDraft[]>([]);
  const itemsRef = useRef<ConversationSourceDraft[]>([]);

  function replace(updater: (current: ConversationSourceDraft[]) => ConversationSourceDraft[]) {
    setItems((current) => {
      const next = updater(current);
      itemsRef.current = next;
      return next;
    });
  }

  function patch(localId: string, update: Partial<ConversationSourceDraft>) {
    replace((current) => current.map((item) => item.localId === localId ? { ...item, ...update } : item));
  }

  function addFiles(files: FileList | File[], expectedKind: 'file' | 'image') {
    if (disabled) return;
    const available = Math.max(0, maxConversationSources - itemsRef.current.length);
    const selected = Array.from(files).slice(0, available);
    if (selected.length === 0) {
      showToast(`A message can carry at most ${maxConversationSources} sources.`, 'error');
      return;
    }
    if (Array.from(files).length > selected.length) showToast(`Kept the first ${available} sources.`);
    for (const file of selected) void begin(file, expectedKind);
  }

  async function addVoice(file: File, transcript: string) {
    if (disabled) return;
    if (itemsRef.current.length >= maxConversationSources) {
      showToast(`A message can carry at most ${maxConversationSources} sources.`, 'error');
      return;
    }
    await begin(file, 'audio', { method: 'web-speech-recognition', text: transcript });
  }

  async function begin(
    file: File,
    expectedKind: SourceMaterialKind,
    provided?: { method: SourceExtractionMethod; text: string },
  ) {
    let localId = '';
    let sourceMaterial: SourceMaterial | undefined;
    try {
      const contract = validateWebSourceFile(file, expectedKind);
      localId = crypto.randomUUID();
      const draft: ConversationSourceDraft = {
        localId,
        file,
        kind: contract.kind,
        mimeType: contract.mimeType,
        status: 'uploading',
        progress: 0.04,
        statusLabel: 'Uploading source',
        text: '',
        extractionMethod: provided?.method || 'file-text',
      };
      replace((current) => [...current, draft]);
      const api = createWebApiClient();
      const upload = await api.sourceMaterialUploadUrl({
        materialKind: contract.kind,
        originalName: file.name,
        mimeType: contract.mimeType,
        byteSize: file.size,
      });
      sourceMaterial = upload.sourceMaterial;
      patch(localId, { sourceMaterial });
      const uploaded = await fetch(upload.uploadUrl, {
        method: upload.method || 'PUT',
        headers: upload.headers,
        body: file,
      });
      if (!uploaded.ok) throw new Error(`Source upload failed (${uploaded.status}).`);
      await api.startSourceMaterialProcessing(sourceMaterial.id);
      patch(localId, { status: 'processing', progress: 0.08, statusLabel: provided ? 'Preparing transcript' : 'Extracting text' });
      const extracted = provided
        ? { method: provided.method, text: validateConfirmedSourceText(provided.text) }
        : await extractSourceText(file, contract.kind, (progress, statusLabel) => patch(localId, { progress, statusLabel }));
      patch(localId, {
        status: 'review',
        progress: 1,
        statusLabel: contract.kind === 'audio' ? 'Confirm the transcript' : 'Confirm the extracted text',
        text: extracted.text,
        extractionMethod: extracted.method,
      });
    } catch (error) {
      const message = sourceErrorMessage(error);
      if (sourceMaterial) {
        await createWebApiClient().failSourceMaterial(sourceMaterial.id, sourceFailureCode(error)).catch(() => undefined);
      }
      if (localId) patch(localId, { status: 'failed', progress: 0, statusLabel: 'Failed', error: message });
      else showToast(message, 'error');
    }
  }

  async function confirm(localId: string) {
    const item = itemsRef.current.find((candidate) => candidate.localId === localId);
    if (!item?.sourceMaterial || item.status !== 'review') return;
    try {
      const text = validateConfirmedSourceText(item.text);
      patch(localId, { status: 'processing', statusLabel: 'Saving confirmed text', error: undefined });
      const response = await createWebApiClient().completeSourceMaterial(item.sourceMaterial.id, {
        confirmedText: text,
        extractionMethod: item.extractionMethod,
      });
      patch(localId, { status: 'ready', statusLabel: 'Ready to send with your message', sourceMaterial: response.sourceMaterial, text });
    } catch (error) {
      patch(localId, { status: 'review', statusLabel: 'Review the confirmed text', error: sourceErrorMessage(error) });
    }
  }

  async function retry(localId: string) {
    const item = itemsRef.current.find((candidate) => candidate.localId === localId);
    if (!item?.sourceMaterial || item.status !== 'failed') return;
    if (item.kind === 'audio') {
      patch(localId, { error: 'A failed transcript needs re-recording. Remove this voice note first.' });
      return;
    }
    try {
      await createWebApiClient().startSourceMaterialProcessing(item.sourceMaterial.id);
      patch(localId, { status: 'processing', statusLabel: 'Re-extracting text', error: undefined });
      const extracted = await extractSourceText(item.file, item.kind, (progress, statusLabel) => patch(localId, { progress, statusLabel }));
      patch(localId, {
        status: 'review',
        progress: 1,
        statusLabel: 'Confirm the extracted text',
        text: extracted.text,
        extractionMethod: extracted.method,
      });
    } catch (error) {
      await createWebApiClient().failSourceMaterial(item.sourceMaterial.id, sourceFailureCode(error)).catch(() => undefined);
      patch(localId, { status: 'failed', progress: 0, statusLabel: 'Failed', error: sourceErrorMessage(error) });
    }
  }

  async function remove(localId: string) {
    const item = itemsRef.current.find((candidate) => candidate.localId === localId);
    if (!item) return;
    if (!item.sourceMaterial) {
      replace((current) => current.filter((candidate) => candidate.localId !== localId));
      return;
    }
    patch(localId, { status: 'removing', statusLabel: 'Removing' });
    try {
      await createWebApiClient().deleteSourceMaterial(item.sourceMaterial.id);
      replace((current) => current.filter((candidate) => candidate.localId !== localId));
    } catch (error) {
      patch(localId, { status: item.status, statusLabel: item.statusLabel, error: sourceErrorMessage(error) });
    }
  }

  const readyIds = useMemo(
    () => items.filter((item) => item.status === 'ready' && item.sourceMaterial).map((item) => item.sourceMaterial!.id),
    [items],
  );

  return {
    addFiles,
    addVoice,
    blocksSend: items.length > 0 && items.some((item) => item.status !== 'ready'),
    confirm,
    consumeReady: () => replace((current) => current.filter((item) => item.status !== 'ready')),
    editText: (localId, text) => patch(localId, { text, error: undefined }),
    items,
    readyIds,
    remove,
    retry,
  };
}

export function ConversationSourceComposer(props: { controller: ConversationSourceController; disabled?: boolean }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const imageInput = useRef<HTMLInputElement>(null);
  const remaining = maxConversationSources - props.controller.items.length;
  return (
    <section className="mira-source-composer" aria-label="Add sources to this message">
      {props.controller.items.length ? <SourceDraftTray controller={props.controller} /> : null}
      <div className="mira-source-actions">
        <button disabled={props.disabled || remaining <= 0} onClick={() => fileInput.current?.click()} type="button"><IconGlyph value="＋" /> File</button>
        <button disabled={props.disabled || remaining <= 0} onClick={() => imageInput.current?.click()} type="button"><IconGlyph value="▧" /> Image</button>
        <VoiceJournalRecorder disabled={props.disabled || remaining <= 0} onCapture={props.controller.addVoice} />
        <span>{remaining > 0 ? `${remaining} more allowed` : 'Source limit reached (3)'}</span>
      </div>
      <input
        ref={fileInput}
        className="mira-visually-hidden"
        accept=".txt,.md,.markdown,.csv,.json,.pdf,text/plain,text/markdown,text/csv,application/json,application/pdf"
        multiple
        onChange={(event) => {
          if (event.currentTarget.files) props.controller.addFiles(event.currentTarget.files, 'file');
          event.currentTarget.value = '';
        }}
        tabIndex={-1}
        type="file"
      />
      <input
        ref={imageInput}
        className="mira-visually-hidden"
        accept="image/png,image/jpeg,image/webp"
        multiple
        onChange={(event) => {
          if (event.currentTarget.files) props.controller.addFiles(event.currentTarget.files, 'image');
          event.currentTarget.value = '';
        }}
        tabIndex={-1}
        type="file"
      />
    </section>
  );
}

function SourceDraftTray(props: { controller: ConversationSourceController }) {
  return (
    <div className="mira-source-tray" aria-live="polite">
      {props.controller.items.map((item) => (
        <article className="mira-source-draft" data-status={item.status} key={item.localId}>
          <header>
            <span aria-hidden="true"><IconGlyph value={sourceIcon(item.kind)} /></span>
            <div><strong>{item.file.name}</strong><small>{item.statusLabel}</small></div>
            <button aria-label={`Remove ${item.file.name}`} disabled={item.status === 'removing'} onClick={() => void props.controller.remove(item.localId)} type="button">×</button>
          </header>
          {item.status === 'uploading' || item.status === 'processing' ? <progress max="1" value={item.progress} /> : null}
          {item.status === 'review' ? (
            <>
              <label>Confirm the text before sending<textarea maxLength={60_000} onChange={(event) => props.controller.editText(item.localId, event.currentTarget.value)} value={item.text} /></label>
              <Button onClick={() => void props.controller.confirm(item.localId)}>Confirm this text</Button>
            </>
          ) : null}
          {item.error ? <p role="alert">{item.error}</p> : null}
          {item.status === 'failed' && item.kind !== 'audio' ? <Button tone="secondary" onClick={() => void props.controller.retry(item.localId)}>Reprocess</Button> : null}
        </article>
      ))}
    </div>
  );
}

export function MessageSourceMaterials(props: {
  sources?: SourceMaterialSummary[];
  onDeleted?: (sourceId: string) => void;
}) {
  const [confirming, setConfirming] = useState('');
  const [deleting, setDeleting] = useState('');
  const [error, setError] = useState('');
  if (!props.sources?.length) return null;
  return (
    <div className="mira-message-sources">
      {props.sources.map((source) => (
        <section data-status={source.status} key={source.id}>
          <span aria-hidden="true"><IconGlyph value={sourceIcon(source.materialKind)} /></span>
          <div><strong>{source.status === 'deleted' ? 'Source deleted' : source.originalName}</strong><small>{sourceStatusText(source)}</small></div>
          {source.status !== 'deleted' ? <button disabled={deleting === source.id} onClick={() => setConfirming(source.id)} type="button">Remove source</button> : null}
          {confirming === source.id ? (
            <div className="mira-source-delete-confirm">
              <p>This deletes the original source and its confirmed text. Past replies, memories, and insights already formed are untouched and can be handled in their own places.</p>
              <button onClick={() => setConfirming('')} type="button">Cancel</button>
              <button onClick={() => void deleteHistoricalSource(source.id)} type="button">Delete</button>
            </div>
          ) : null}
        </section>
      ))}
      {error ? <p role="alert">{error}</p> : null}
    </div>
  );

  async function deleteHistoricalSource(sourceId: string) {
    setDeleting(sourceId);
    setError('');
    try {
      await createWebApiClient().deleteSourceMaterial(sourceId);
      setConfirming('');
      props.onDeleted?.(sourceId);
    } catch (deleteError) {
      setError(sourceErrorMessage(deleteError));
    } finally {
      setDeleting('');
    }
  }
}

function sourceIcon(kind: SourceMaterialKind): string {
  if (kind === 'image') return '▧';
  if (kind === 'audio') return '◉';
  return '≡';
}

function sourceStatusText(source: SourceMaterialSummary): string {
  if (source.status === 'deleted') return 'Original source and confirmed text are gone';
  if (source.materialKind === 'audio') return 'Voice note · text confirmed';
  if (source.materialKind === 'image') return 'Image · text confirmed';
  return source.mimeType === 'application/pdf' ? 'PDF · text confirmed' : 'File · text confirmed';
}

function sourceFailureCode(error: unknown): string {
  if (error instanceof SourceMaterialInputError) return error.code.replace(/[^a-z0-9_-]/giu, '_').toLowerCase();
  return 'source_processing_failed';
}

function sourceErrorMessage(error: unknown): string {
  return error instanceof Error && error.message.trim() ? error.message : 'The source could not be processed. Please try again.';
}
